import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import notify from '@/lib/notify';
import { Persona } from '../../types';
import PersonaCard from './PersonaCard';
import PersonaDetailModal from './PersonaDetailModal';
import AddPersonaPlaceholderCard from './AddPersonaPlaceholderCard';
import PersonaFormModal from './PersonaFormModal';
// Importer le nouveau hook CRUD et le type pour la fonction de liaison
import { usePersonasCRUD } from '../../hooks/usePersonasCRUD';

// Rendre les props interactives optionnelles et ajouter readOnly
interface PersonaDisplaySectionProps {
  personas: Persona[];
  updateAssociatedPersonas?: (personaIds: string[]) => Promise<boolean>; // Optionnel
  fetchAssociatedPersonas?: () => Promise<void>; // Optionnel
  readOnly?: boolean; // Nouvelle prop pour le mode lecture seule
  showTitle?: boolean; // Nouvelle prop pour contrôler l'affichage du titre
  showButton?: boolean; // Nouvelle prop pour contrôler l'affichage du bouton
  renderAddButton?: (onClick: () => void, disabled: boolean) => React.ReactNode; // Fonction pour rendre un bouton custom
}

const PersonaDisplaySection: React.FC<PersonaDisplaySectionProps> = ({
    personas,
    // Ligne dupliquée supprimée
    updateAssociatedPersonas, // Récupérer la prop optionnelle
    fetchAssociatedPersonas, // Récupérer la prop optionnelle
    readOnly = false, // Valeur par défaut pour readOnly
    showTitle = true, // Valeur par défaut pour showTitle
    showButton = true, // Valeur par défaut pour showButton
    renderAddButton,
}) => {
  const { t } = useTranslation();
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState<Partial<Persona> | null>(null);
  // Utiliser le hook CRUD (ajouter deletePersona et isDeleting)
  const { createPersona, updatePersona, deletePersona, isCreating, isUpdating, isDeleting, error: crudError } = usePersonasCRUD();

  const handleCardClick = (persona: Persona) => {
    setSelectedPersona(persona);
    setIsDetailModalOpen(true);
  };

  // Fonction pour ouvrir la modale d'édition
  const handleEditPersonaClick = (personaToEdit: Persona) => {
      console.log("Ouverture de la modale d'édition pour:", personaToEdit);
      setEditingPersona(personaToEdit); // Pré-remplir avec les données existantes
      setIsFormModalOpen(true); // Ouvre la modale de formulaire
  };

  // Fonction pour gérer la suppression (appel API direct via hook)
  const handleDeletePersona = async (personaId: string) => {
      // Utiliser la fonction deletePersona déjà obtenue du hook usePersonasCRUD (ligne 31)
      // Note: La confirmation est déjà dans PersonaDetailModal
      try {
          const success = await deletePersona(personaId); // Appeler la suppression API
          if (success) {
              // Rafraîchir la liste des personas associés après suppression (seulement si la fonction est fournie)
              if (updateAssociatedPersonas) {
                  const currentPersonaIds = personas.map(p => p.id).filter(id => id !== personaId);
                  await updateAssociatedPersonas(currentPersonaIds);
              } else if (fetchAssociatedPersonas) {
                  // Alternative: juste rafraîchir si updateAssociatedPersonas n'est pas là mais fetch l'est
                  await fetchAssociatedPersonas();
              }
              // Le re-fetch devrait mettre à jour la liste affichée
          } else {
               notify.error(t('useCases.deleteError', { error: crudError || '' }));
          }
      } catch (error) { // Double sécurité, même si le hook gère déjà
          console.error("Erreur lors de la suppression du persona (depuis DisplaySection):", error);
          notify.error(t('useCases.deleteError', { error: crudError || '' }));
      }
  };


  const handleAddPersonaClick = () => {
    console.log("Ouverture de la modale d'ajout de persona.");
    setEditingPersona(null); // Assure qu'on est en mode ajout
    setIsFormModalOpen(true); // Ouvre la modale de formulaire
  };


  // Fonction pour gérer la soumission du formulaire d'ajout/édition (modifiée)
  // Utiliser Partial<Persona> pour le type formData car tous les champs sauf name sont optionnels dans le formulaire/payload
  const handleSavePersona = useCallback(async (formData: Partial<Persona>) => {
      // Construire le payload en s'assurant que les champs optionnels sont null si vides
      // et que le nom est présent (devrait être validé par le formulaire)
      // Utiliser les noms de champs corrects du type Persona
      const payload = {
          name: formData.name || 'Nom Manquant', // Validation requise dans le formulaire
          role: formData.role || null,
          description: formData.description || null, // Ajouter description
          citation: formData.citation || null, // Utiliser citation
          objectif: formData.objectif || null, // Utiliser objectif (string)
          frustration: formData.frustration || null, // Utiliser frustration (string)
      };

      let success = false;
      let newPersonaId: string | null = null;

      if (editingPersona?.id) {
          // Logique de mise à jour via API
          console.log("Mise à jour du persona via API:", payload);
          const updatedPersona = await updatePersona(editingPersona.id, payload);
          if (updatedPersona) {
              success = true;
              // Rafraîchir la liste des personas associés (seulement si la fonction est fournie)
              if (fetchAssociatedPersonas) {
                  fetchAssociatedPersonas(); // Appel pour rafraîchir
              }
          }
      } else {
          // Logique d'ajout via API (seulement si les fonctions sont fournies)
          if (!createPersona || !updateAssociatedPersonas) {
              console.warn("Fonctions createPersona ou updateAssociatedPersonas manquantes pour l'ajout.");
              notify.error(t('useCases.cannotAddInContext'));
              return; // Ne rien faire si les fonctions nécessaires manquent
          }
          console.log("Ajout du nouveau persona via API:", payload);
          const createdPersona = await createPersona(payload);
          if (createdPersona) {
              success = true;
              newPersonaId = createdPersona.id;
              // Lier automatiquement le nouveau persona au cas d'usage actuel
              const currentPersonaIds = personas.map(p => p.id);
              // Vérifier à nouveau avant d'appeler
              if (updateAssociatedPersonas) {
                  await updateAssociatedPersonas([...currentPersonaIds, newPersonaId]);
              }
              // Le re-fetch dans updateAssociatedPersonas devrait mettre à jour la liste
          }
      }

      if (success) {
          setIsFormModalOpen(false); // Fermer la modale après succès
          setEditingPersona(null); // Réinitialiser l'édition
      } else {
          // Afficher l'erreur crudError (peut être fait dans la modale ou ici)
          notify.error(t('useCases.saveError', { error: crudError || '' }));
      }
  }, [editingPersona, createPersona, updatePersona, personas, updateAssociatedPersonas, fetchAssociatedPersonas, crudError]); // Ajouter fetchAssociatedPersonas aux dépendances

  // Déterminer l'état de chargement global pour la modale (inclure isDeleting)
  const isProcessing = isCreating || isUpdating || isDeleting;

  return (
    <div>
      {showTitle && (
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-foreground">{t('useCases.personasTarget', 'Target Personas')}</h3>
          {!readOnly && showButton && (
              <button
                onClick={handleAddPersonaClick}
                disabled={isProcessing || !createPersona || !updateAssociatedPersonas}
                className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                title={!createPersona || !updateAssociatedPersonas ? t('useCases.featureUnavailable') : t('useCases.addNewPersona')}
              >
                {t('useCases.addPersona', 'Add Persona')}
              </button>
          )}
        </div>
      )}
      
      {!showTitle && !readOnly && showButton && (
        <div className="flex justify-end mb-4">
          <button
            onClick={handleAddPersonaClick}
            disabled={isProcessing || !createPersona || !updateAssociatedPersonas}
            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            title={!createPersona || !updateAssociatedPersonas ? t('useCases.featureUnavailable') : t('useCases.addNewPersona')}
          >
            {t('useCases.addPersona', 'Add Persona')}
          </button>
        </div>
      )}
      
      {/* Bouton custom rendu par le parent */}
      {renderAddButton && !readOnly && (
        <div className="mb-4">
          {renderAddButton(handleAddPersonaClick, isProcessing || !createPersona || !updateAssociatedPersonas)}
        </div>
      )}
      
      {/* Afficher la grille ou un message si vide */}
       {personas && personas.length > 0 ? (
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Mapper les personas existants */}
        {personas && personas.map(p => (
          <PersonaCard
            key={p.id}
            persona={p}
            onClick={() => handleCardClick(p)}
          />
        ))}
        {/* Afficher la carte placeholder seulement si pas en lecture seule */}
        {!readOnly && <AddPersonaPlaceholderCard onClick={handleAddPersonaClick} />}
      </div>
       ) : (
            <p className="text-muted-foreground italic text-sm">{t('useCases.noPersonasAssociated', 'No personas associated.')}</p>
       )}

      {/* Modale pour afficher les détails (passer onEdit et onDelete conditionnellement) */}
      <PersonaDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        persona={selectedPersona}
        // Passer des no-op en mode lecture seule
        onEdit={readOnly ? (() => {}) : handleEditPersonaClick}
        onDelete={readOnly ? (async () => {}) : handleDeletePersona}
        onAvatarUpdated={
          !readOnly
            ? async (updated) => {
                // Met à jour immédiatement le persona sélectionné pour rafraîchir l'image dans la modale
                setSelectedPersona(updated);
                // Optionnel : recharger la liste complète des personas si fourni
                if (fetchAssociatedPersonas) {
                  await fetchAssociatedPersonas();
                }
              }
            : undefined
        }
        readOnly={readOnly}
      />
      {/* Modale pour ajouter/éditer (ne s'ouvre que si !readOnly via handleAdd/handleEdit) */}
      {!readOnly && (
          <PersonaFormModal
            isOpen={isFormModalOpen}
            onClose={() => setIsFormModalOpen(false)}
            onSubmit={handleSavePersona}
            initialData={editingPersona ?? undefined}
            isLoading={isProcessing}
            error={crudError}
          />
      )}
    </div>
  );
};

export default PersonaDisplaySection;
